// Редовият модел на „Продажби по периоди".
//
// Един ред = един отрязък от времето, както го връща BE-то. Тук се строи
// етикетът му (BE носи само ключа и полетата — час, ден от седмицата, месец,
// дата — за да може етикетът да е на езика на интерфейса), смятат се
// производните колони (средна цена, средна сметка) и сумите по метод на
// ПОКАЗАНИТЕ редове за колоните по метод.

import dayjs from "dayjs";

import { parseDecimal } from "../../../../components/dataTable";
import type {
  SalesByPeriodsMode,
  SalesByPeriodsRow,
  SalesByPeriodsUnit,
} from "../../../../api/reportsSalesByPeriods";

export interface PeriodDay {
  date: string;
  bills: number;
  revenue: number;
}

export interface PeriodRow {
  /** Сортируемият ключ на BE — редовете вървят хронологично по него. */
  key: string;
  /** Етикетът на реда („13:00 – 13:59", „понеделник", „29.06.2026"). */
  label: string;
  /** Втори ред под етикета (денят от седмицата при „по дати"). */
  sublabel: string | null;
  bills: number;
  /** Сметките с ненулев оборот — знаменател на средната сметка. */
  paidBills: number;
  /** Различни клиенти със сметка в периода. */
  clients: number;
  /** Оборотът по сметки с клиент. */
  clientRevenue: number;
  /** Оборот с клиент ÷ клиенти. */
  clientAvg: number;
  quantity: number;
  revenue: number;
  /** Частта от оборота по сметки с касова бележка. */
  fiscalized: number;
  baseAmount: number;
  /** Отстъпки и надбавки поотделно (BE ги дели по знака на всеки ред). */
  discount: number;
  markup: number;
  vatAmount: number;
  netAmount: number;
  /** Оборот ÷ количество. */
  avgPrice: number;
  /** Оборот ÷ сметки без тези с 0.00. */
  avgBill: number;
  methodAmounts: Map<number, number>;
  methodCounts: Map<number, number>;
  /** Оборот по станция — колоните по станция; сборът е `revenue`. */
  stationAmounts: Map<number, number>;
  days: PeriodDay[];
}

const pad2 = (value: number) => String(value).padStart(2, "0");

/** „13:00 – 13:59" — часът е реален час от денонощието. */
const hourLabel = (hour: number) => `${pad2(hour)}:00 – ${pad2(hour)}:59`;

/**
 * Името на деня/месеца през `Intl`, а НЕ през dayjs: в проекта dayjs върви без
 * заредени локали и `format("dddd")` би дало английско име и на български.
 */
const weekdayName = (weekday: number, language: string, style: "long" | "short") =>
  // 2024-01-01 е понеделник — BE брои понеделник = 0.
  new Intl.DateTimeFormat(language, { weekday: style }).format(
    new Date(2024, 0, 1 + weekday),
  );

const monthName = (
  month: number,
  language: string,
  style: "long" | "short" = "long",
) =>
  new Intl.DateTimeFormat(language, { month: style }).format(
    new Date(2024, month - 1, 1),
  );

const capitalize = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

export interface PeriodLabels {
  label: string;
  sublabel: string | null;
  chartLabel: string;
  chartSublabel: string | null;
}

const labels = (
  label: string,
  sublabel: string | null = null,
  chartLabel: string = label,
  chartSublabel: string | null = null,
): PeriodLabels => ({ label, sublabel, chartLabel, chartSublabel });

/**
 * Етикетът на реда според режима и единицата — пълният за таблицата и
 * късият за оста на графиката, където 18 колонки по „13:00 – 13:59" не се
 * събират и се режат до „13:00 – …".
 */
export const periodLabelOf = (
  row: Pick<SalesByPeriodsRow, "hour" | "weekday" | "month" | "year" | "date" | "key">,
  mode: SalesByPeriodsMode,
  unit: SalesByPeriodsUnit,
  language: string,
): PeriodLabels => {
  if (mode === "grouped") {
    if (unit === "hour" && row.hour !== null) {
      return labels(hourLabel(row.hour), null, `${pad2(row.hour)}:00`);
    }
    if (unit === "weekday" && row.weekday !== null) {
      return labels(
        capitalize(weekdayName(row.weekday, language, "long")),
        null,
        weekdayName(row.weekday, language, "short"),
      );
    }
    if (unit === "month" && row.month !== null) {
      return labels(
        capitalize(monthName(row.month, language)),
        null,
        monthName(row.month, language, "short"),
      );
    }
    return labels(row.key);
  }

  if (unit === "hour" && row.date && row.hour !== null) {
    const day = dayjs(row.date);
    return labels(
      hourLabel(row.hour),
      day.format("DD.MM.YYYY"),
      `${pad2(row.hour)}:00`,
      day.format("DD.MM"),
    );
  }
  if (unit === "date" && row.date) {
    const day = dayjs(row.date);
    const weekday = (day.day() + 6) % 7;
    return labels(
      day.format("DD.MM.YYYY"),
      capitalize(weekdayName(weekday, language, "long")),
      day.format("DD.MM"),
      weekdayName(weekday, language, "short"),
    );
  }
  if (unit === "month" && row.month !== null && row.year !== null) {
    return labels(
      `${capitalize(monthName(row.month, language))} ${row.year}`,
      null,
      `${monthName(row.month, language, "short")} ${row.year}`,
    );
  }
  if (unit === "year" && row.year !== null) {
    return labels(String(row.year));
  }
  return labels(row.key);
};

const safeDivide = (numerator: number, denominator: number) =>
  denominator > 0 ? numerator / denominator : 0;

export const buildPeriodRows = (
  rows: SalesByPeriodsRow[],
  mode: SalesByPeriodsMode,
  unit: SalesByPeriodsUnit,
  language: string,
): PeriodRow[] =>
  rows.map((row) => {
    const revenue = parseDecimal(row.revenue);
    const quantity = parseDecimal(row.quantity);
    const clientRevenue = parseDecimal(row.client_revenue);
    return {
      key: row.key,
      ...periodLabelOf(row, mode, unit, language),
      bills: row.bills,
      paidBills: row.paid_bills,
      clients: row.clients,
      clientRevenue,
      clientAvg: safeDivide(clientRevenue, row.clients),
      quantity,
      revenue,
      fiscalized: parseDecimal(row.fiscalized_revenue),
      baseAmount: parseDecimal(row.base_amount),
      discount: parseDecimal(row.discount),
      markup: parseDecimal(row.markup),
      vatAmount: parseDecimal(row.vat_amount),
      netAmount: parseDecimal(row.net_amount),
      avgPrice: safeDivide(revenue, quantity),
      avgBill: safeDivide(revenue, row.paid_bills),
      methodAmounts: new Map(
        row.methods.map((method) => [
          method.payment_method_id,
          parseDecimal(method.amount),
        ]),
      ),
      methodCounts: new Map(
        row.methods.map((method) => [method.payment_method_id, method.count]),
      ),
      stationAmounts: new Map(
        (row.stations ?? []).map((station) => [
          station.station_id,
          parseDecimal(station.amount),
        ]),
      ),
      days: row.days.map((day) => ({
        date: day.date,
        bills: day.bills,
        revenue: parseDecimal(day.revenue),
      })),
    };
  });

/** Сумите по метод на плащане на показаните редове — за колоните по метод. */
export const methodTotalsOfRows = (
  rows: PeriodRow[],
): Map<number, { amount: number; count: number }> => {
  const totals = new Map<number, { amount: number; count: number }>();
  for (const row of rows) {
    for (const [id, amount] of row.methodAmounts) {
      const entry = totals.get(id) ?? { amount: 0, count: 0 };
      entry.amount += amount;
      entry.count += row.methodCounts.get(id) ?? 0;
      totals.set(id, entry);
    }
  }
  return totals;
};
